import React from 'react'

import { t } from 'i18next'

import {
  TFailPage,
  TPaymentScreen,
  TPaymentSuccess,
  TPageReceiptData,
  TSelectPlan,
  TAlreadyHasAccountPage,
  TAlreadyPaidPage,
} from '@/config/types'
import { SelectPlanBlockType } from '@/enums/payments'
import { PaymentScreenVersion } from '@/enums/paymentScreenVersion'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { downloadImage } from '@/utils/imageUtils'
import { checkSelectPlanBlocks } from '@/utils/pageConfigUtils'

import { Accordion } from '@/components/accordion'
import { BeforePlanSubscriptionC } from '@/components/beforePlanSubscription/typeC'
import { ProgressDescription } from '@/components/descriptionBodyProgress'
import { ExtraDiscountBanner } from '@/components/extraDiscountBanner'
import { ExtraDiscountBannerProps } from '@/components/extraDiscountBanner/types'
import { ListWithIcons, TListWithIconsProps } from '@/components/listWithIcons'
import { MoneyGuarantee } from '@/components/moneyGuarantee'
import { MoneyBackInformation } from '@/components/policyLinkBlock'
import { ReviewTypeF } from '@/components/review'
import { Bag } from '@/shared/icons/Bag'
import { FailRectRounded } from '@/shared/icons/FailRectRounded'
import { Gift } from '@/shared/icons/Gift'
import { RectRoundCheckbox5 } from '@/shared/icons/RectRoundCheckbox5'
import { Watch1 } from '@/shared/icons/Watch1'
import checked from '@/shared/images/checked.png'
import gift_2 from '@/shared/images/gift-2.png'
import guarantee from '@/shared/images/guarantee.png'
import manual_step_1 from '@/shared/images/manual-step-1.png'
import manual_step_2 from '@/shared/images/manual-step-2.png'
import { bodyProgress1, fotoBodyProgress } from '@/shared/images/organic-fit'

import { paymentScreenVisitedKey } from '@/core/constants'
import { FotoBodyProgress } from '@/orgfit/components/fotoBodyProgress'
import { TimerStatic } from '@/orgfit/components/timerStatic'
import { TTimerStaticProps } from '@/orgfit/components/timerStatic/types'

import { EXPERIMENT_NAME, CANCELATION_POLICY_LINK, PAYMENT_SCREEN_VERSION } from './constants'
import { plans } from '../planDetails'
import { getItem } from '../storage'

const bodyProgressSrc = downloadImage(bodyProgress1)
const guaranteeSrc = downloadImage(guarantee)
const fotoSrc = downloadImage(fotoBodyProgress)
const checkedSrc = downloadImage(checked)
const gift2Src = downloadImage(gift_2)

const paymentModalId = 'payment-modal-window'

export const selectPlanPage: TSelectPlan = {
  blocks: [
    () => {
      // sticky section
      const hasExtraDiscountPlan = plans.some((plan) => !!plan.extraDiscountPlan)
      const extraDiscountBannerConf = window.config.EXTRA_DISCOUNT_BANNER

      if (extraDiscountBannerConf && hasExtraDiscountPlan && getItem(paymentScreenVisitedKey)) {
        const title = t('selectPlanScreen.extraDiscountBanner.title').replace(
          '{oldDiscount}',
          extraDiscountBannerConf.expired.toString()
        )
        const description = t('selectPlanScreen.extraDiscountBanner.description').replace(
          '{newDiscount}',
          extraDiscountBannerConf.current.toString()
        )

        const props: ExtraDiscountBannerProps = {
          title: title,
          description: description,
          iconNode: <img src={gift2Src.src} alt="discount" />,
        }
        return <ExtraDiscountBanner {...props} />
      }
    },
    () => {
      const props: TTimerStaticProps = {
        descriptionTimer: t('selectPlanScreen.timer.descriptionTimer'),
        initialMinutes: '10',
        interval: t('selectPlanScreen.timer.minutes'),
        watchComponent: Watch1,
      }
      return <TimerStatic key="additional-timer" {...props} />
    },
    () => {
      return (
        <div key="progress-image" className="progress-container">
          <div className="title-progress-container">
            <div className="now">{t('selectPlanScreen.progressContainer.title.before')}</div>
            <div className="goal">{t('selectPlanScreen.progressContainer.title.after')}</div>
          </div>
          <img src={bodyProgressSrc.src} alt="body-progress" />
        </div>
      )
    },

    () => {
      const progressDescriptionBlock = {
        beforeSections: [
          {
            name: t('selectPlanScreen.progressContainer.levelBodyFat.title'),
            value: t('selectPlanScreen.progressContainer.levelBodyFat.value.before'),
          },
          {
            name: t('selectPlanScreen.progressContainer.levelPilates.title'),
            value: t('selectPlanScreen.progressContainer.levelPilates.value.before'),
            hasPanel: true,
          },
        ],
        afterSections: [
          {
            name: t('selectPlanScreen.progressContainer.levelBodyFat.title'),
            value: t('selectPlanScreen.progressContainer.levelBodyFat.value.after'),
          },
          {
            name: t('selectPlanScreen.progressContainer.levelPilates.title'),
            value: t('selectPlanScreen.progressContainer.levelPilates.value.after'),
            hasPanel: true,
          },
        ],
      }
      return (
        <div key="progress-description" className="progress-description-container">
          <ProgressDescription progressDescriptionBlock={progressDescriptionBlock} />
        </div>
      )
    },

    {
      key: 'payment-modal-window',
      blockType: SelectPlanBlockType.paymentModalWindow,
      modalId: paymentModalId,
    },
    {
      key: 'select-plan-1',
      blockType: SelectPlanBlockType.selectPlanWithPaymentForcedApplePay,
      walletButtonTheme: StripeWalletButtonTheme.dark,
      modalId: paymentModalId,
      submitButtonClassName: 'prime',
      specificPlanDetailsTitle: t('selectPlanScreen.specificPlanDetailsTitle'),
    },

    () => {
      return (
        <div key="guarantee" className="guarantee-container">
          <div className="title-guarantee">
            <RectRoundCheckbox5 />
            &nbsp;{t('selectPlanScreen.guaranteeTitle')}
          </div>
          <img src={guaranteeSrc.src} alt="guarantee" />
        </div>
      )
    },

    () => {
      const props: TListWithIconsProps = {
        className: 'bonus-container',
        items: [
          {
            id: 1,
            iconNode: <Gift />,
            title: t('selectPlanScreen.bonus.title'),
            description: t('selectPlanScreen.bonus.description'),
          },
        ],
      }
      return (
        <div className="bonus-section" key="free-bonus">
          <ListWithIcons {...props} />
        </div>
      )
    },

    () => {
      const props: TListWithIconsProps = {
        className: 'advantages-container',
        mainTitle: t('selectPlanScreen.advantages.0.mainTitle'),
        items: [
          {
            id: 1,
            iconNode: <img src={checkedSrc.src} alt="checked" />,
            title: t('selectPlanScreen.advantages.1.title'),
          },
          {
            id: 2,
            iconNode: <img src={checked} alt="checked" />,
            title: t('selectPlanScreen.advantages.2.title'),
          },
          {
            id: 3,
            iconNode: <img src={checked} alt="checked" />,
            title: t('selectPlanScreen.advantages.3.title'),
          },
        ],
      }
      return (
        <div className="advantages-section" key="what-you-get-1">
          <ListWithIcons {...props} />
        </div>
      )
    },

    () => {
      const fotoBodyBlock = {
        title: t('selectPlanScreen.bodyProgress.title'),
        period: t('selectPlanScreen.bodyProgress.period'),
        name: t('selectPlanScreen.bodyProgress.name'),
        weight: t('selectPlanScreen.bodyProgress.weight'),
        description: t('selectPlanScreen.bodyProgress.description'),
      }
      return (
        <div className="foto-body-progress-container" key="foto-body-progress">
          <FotoBodyProgress fotoBodyBlock={fotoBodyBlock} foto={fotoSrc} />
        </div>
      )
    },

    () => {
      const reviewsTitle = t('selectPlanScreen.reviewsTitle')
      const reviews = [
        {
          name: t('selectPlanScreen.reviews.0.name'),
          description: t('selectPlanScreen.reviews.0.description'),
        },
        {
          name: t('selectPlanScreen.reviews.1.name'),
          description: t('selectPlanScreen.reviews.1.description'),
        },
        {
          name: t('selectPlanScreen.reviews.2.name'),
          description: t('selectPlanScreen.reviews.2.description'),
        },
      ]

      return <ReviewTypeF key="review" reviewsTitle={reviewsTitle} reviews={reviews} />
    },

    {
      key: 'select-plan-2',
      blockType: SelectPlanBlockType.selectPlanWithPaymentForcedApplePay,
      walletButtonTheme: StripeWalletButtonTheme.dark,
      modalId: paymentModalId,
      submitButtonClassName: 'prime',
    },

    () => {
      return (
        <div key="guarantee-1" className="guarantee-container">
          <div className="title-guarantee">
            <RectRoundCheckbox5 />
            &nbsp; {t('selectPlanScreen.guaranteeTitle')}
          </div>
          <img src={guaranteeSrc.src} alt="guarantee" />
        </div>
      )
    },

    () => {
      return (
        <div className="faq" key="faq">
          <div className="title">{t('selectPlanScreen.accordion.title')}</div>
          <div className="questions-list">
            <Accordion
              title={t('selectPlanScreen.accordion.firstBlock.title')}
              body={t('selectPlanScreen.accordion.firstBlock.body')}
            />
            <Accordion
              title={t('selectPlanScreen.accordion.secondBlock.title')}
              body={t('selectPlanScreen.accordion.secondBlock.body')}
            />
            <Accordion
              title={t('selectPlanScreen.accordion.thirdBlock.title')}
              body={t('selectPlanScreen.accordion.thirdBlock.body')}
            />
          </div>
        </div>
      )
    },
    () => {
      const guarantee = t('selectPlanScreen.moneyBackGuarantee.title')
      const moneyBackBlock = {
        firstPart: t('selectPlanScreen.moneyBackGuarantee.firstPart'),
        secondPart: `${t(
          'selectPlanScreen.moneyBackGuarantee.secondPart.0'
        )}<a href="${CANCELATION_POLICY_LINK}">${t(
          'selectPlanScreen.moneyBackGuarantee.secondPart.1'
        )}</a>.`,
        image: <Bag />,
      }
      return (
        <div className="money-guarantee-container" key="money-guarantee">
          <MoneyGuarantee
            guarantee={guarantee}
            firstPart={moneyBackBlock.firstPart}
            secondPart={moneyBackBlock.secondPart}
            image={moneyBackBlock.image}
          />
        </div>
      )
    },
    {
      key: 'select-plan-3',
      blockType: SelectPlanBlockType.selectPlanWithPaymentForcedApplePay,
      walletButtonTheme: StripeWalletButtonTheme.dark,
      anchor: true,
      modalId: paymentModalId,
      submitButtonClassName: 'prime',
    },
    () => {
      return (
        <div key="guarantee-3" className="guarantee-container">
          <div className="title-guarantee">
            <RectRoundCheckbox5 />
            &nbsp; {t('selectPlanScreen.guaranteeTitle')}
          </div>
          <img src={guaranteeSrc.src} alt="guarantee" />
        </div>
      )
    },
  ],
  title: t('selectPlanScreen.title'),
  planDetailsTitle: t('selectPlanScreen.planDetailsTitle'),
  buttonName: t('selectPlanScreen.buttonName'),
  plansDetails: plans,
  isShowInput: true,
  showSelectPlanBlockV2: false,
  concatPrice: true,
  afterSelectPlanBlocks: [
    () => {
      return <MoneyBackInformation key="money-back" />
    },
  ],
  afterButtonBlocks: [
    (selectedPlan) => {
      if (!selectedPlan.hasTrial()) {
        return <br />
      }
      return <BeforePlanSubscriptionC key="before-plan" selectedPlan={selectedPlan} />
    },
  ],
}
checkSelectPlanBlocks(selectPlanPage.blocks)

function getReceiptData(): TPageReceiptData {
  const receiptData: TPageReceiptData = {}
  for (const i in plans) {
    const plan = plans[i]

    if (!plan.crossedPrice) {
      continue
    }

    const price = plan.trialPrice || plan.price
    const discountAmount = plan.currencySign + (plan.crossedPrice - price).toFixed(2)
    const formatPriceBasedOnVersion = (price: number, currencySign: string): string => {
      return PAYMENT_SCREEN_VERSION === PaymentScreenVersion.v3_4
        ? `${price.toFixed(2)} USD`
        : `${currencySign}${price.toFixed(2)}`
    }

    receiptData[plan.planId] = {
      priceInfo: [
        {
          textPlan: t('paymentScreenPage.receiptData.planName'),
          textPrice: formatPriceBasedOnVersion(plan.crossedPrice, plan.currencySign),
        },
        {
          textPlan: t('paymentScreenPage.receiptData.discountRow').replace(
            '{discountPercentage}',
            plan.discountPercentage || ''
          ),
          textPrice: discountAmount,
          extraClassName: 'discount',
          priceExtraClassName: 'highlight',
        },
      ],
      totalPrice: {
        textPlan: t(
          PAYMENT_SCREEN_VERSION === PaymentScreenVersion.v3_4
            ? 'paymentScreenPage.receiptData.totalTextV4'
            : 'paymentScreenPage.receiptData.totalText'
        ),
        periodTextPlan: !plan.hasTrial()
          ? t('paymentScreenPage.receiptData.periodTextPlan').replace(
              '{planDuration}',
              plan.duration
            )
          : '',
        textPrice: formatPriceBasedOnVersion(price, plan.currencySign),
        weekTextPrice:
          plan.periodPrice && plan.discountPercentage
            ? t('paymentScreenPage.receiptData.discountSavedAmount')
                .replace('{amount}', discountAmount)
                .replace('{percentage}', plan.discountPercentage)
            : '',
      },
    }
  }

  return receiptData
}

export const paymentScreenPage: TPaymentScreen = {
  pageTitle: t('paymentScreenPage.pageTitle'),
  discount: t('paymentScreenPage.discount'),
  experimentName: EXPERIMENT_NAME,
  receiptData: getReceiptData(),
  cardButtonName: t('paymentScreenPage.cardButtonName'),
  subscriptionsDetails: t('paymentScreenPage.subscriptionsDetails'),
  showCancelContainer: false,
  walletButtonConfig: {
    underForm: true,
    showDelimiter: false,
    delimiterText: t('paymentScreenPage.delimiterText'),
  },
  stripeFormConfig: {
    paymentButtonText: t('paymentScreenPage.paymentButtonText'),
  },
  showCompanyContacts: false,
}

export const paymentSuccessPage: TPaymentSuccess = {
  loadingTitle: t('paymentSuccessPage.loadingTitle'),
  pageTitle: t('paymentSuccessPage.pageTitle'),
  manualTitle: t('paymentSuccessPage.manualTitle'),
  manualHeader: t('paymentSuccessPage.manualHeader'),
  followPassword: t('paymentSuccessPage.followPassword'),
  manualSteps: [
    {
      id: 1,
      title: t('paymentSuccessPage.manualSteps.0.title'),
      iosTitle: t('paymentSuccessPage.manualSteps.0.iosTitle'),
      androidTitle: t('paymentSuccessPage.manualSteps.0.androidTitle'),
    },
    {
      id: 2,
      title: t('paymentSuccessPage.manualSteps.1.title'),
    },
    {
      id: 3,
      title: t('paymentSuccessPage.manualSteps.2.title'),
    },
  ],
  manualProvidedEmail: [
    {
      id: 1,
      title: t('paymentSuccessPage.manualProvidedEmail.0.title'),
    },
    {
      id: 2,
      title: t('paymentSuccessPage.manualProvidedEmail.1.title'),
    },
    {
      id: 3,
      title: t('paymentSuccessPage.manualProvidedEmail.2.title'),
    },
    { id: 4, title: t('paymentSuccessPage.manualProvidedEmail.3.title') },
  ],
  manualStepsPartialCredentials: [
    {
      id: 1,
      title: t('paymentSuccessPage.manualStepsPartialCredentials.0.title'),
    },
    {
      id: 2,
      title: t('paymentSuccessPage.manualStepsPartialCredentials.1.title'),
    },
    {
      id: 3,
      title: t('paymentSuccessPage.manualStepsPartialCredentials.2.title'),
    },
  ],
  screenshots: { first: manual_step_1, second: manual_step_2 },
  plansDetails: plans,
  isShowPageProgressBar: true,
  ltpStep: {
    title: t('paymentSuccessPage.ltpStep.title'),
  },
}

export const paymentFailPage: TFailPage = {
  paymentTitle: t('paymentFailPage.paymentTitle'),
  pageTitle: t('paymentFailPage.pageTitle'),
  message: t('paymentFailPage.message'),
  buttonName: t('paymentFailPage.buttonName'),
  iconNode: <FailRectRounded strokeColor="red" />,
  plansDetails: plans,
}

export const alreadyHasAccountPage: TAlreadyHasAccountPage = {
  title: t('alreadyHasAccountPage.title'),
  pageTitle: t('alreadyHasAccountPage.pageTitle'),
  buttonName: t('alreadyHasAccountPage.buttonName'),
  manualSteps: [
    {
      id: 1,
      title: t('alreadyHasAccountPage.manualSteps.0.title'),
    },
    {
      id: 2,
      title: t('alreadyHasAccountPage.manualSteps.1.title'),
    },
    {
      id: 3,
      title: t('alreadyHasAccountPage.manualSteps.2.title'),
    },
    {
      id: 4,
      hideNumber: true,
      title: t('alreadyHasAccountPage.manualSteps.3.title'),
    },
  ],
}

export const alreadyPaidPage: TAlreadyPaidPage = {
  title: t('alreadyPaidPage.title'),
  pageTitle: t('alreadyPaidPage.pageTitle'),
  text: (email) => `${t('alreadyPaidPage.text')}${email}.`,
  contactsText: t('alreadyPaidPage.contactsText'),
}
